import { useNavigate } from 'react-router-dom'

import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  ProjectionKind,
  TrainingsCategory,
  useTrainingsDataTable,
} from 'generated/resources/datatable.graphql'
import { CompetencyFragment } from 'generated/resources/graphql'

import DataTable from 'modules/core/components/DataTable'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { ItemsTags } from 'modules/trainings/components/Shared/ItemsTags'
import { categoriesFilter } from 'modules/trainings/components/TrainingsDataTable/categoriesFilter'
import { competenciesFilter } from 'modules/trainings/components/TrainingsDataTable/competenciesFilter'

import { EndDate } from './EndDate'
import { Modality } from './Modality'
import { StartDate } from './StartDate'
import { dateFilter } from './dateFilter'
import { modalityFilter } from './modalityFilter'

interface Props {
  categories: TrainingsCategory[] | undefined
  competencies: CompetencyFragment[] | undefined
}

export const CatalogDatatable = ({ categories, competencies }: React.PropsWithChildren<Props>) => {
  const { search, onSearch, debouncedSearch } = useDebouncedSearch()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const onRequestTraining = (id: number) => {
    track(trackingEvents.training.trainingRequest.addTrainingRequest.add, {
      from: 'employee_training_catalog_empty_space',
    })
    navigate(navigation.trainings.catalog.list.fromCatalog.fullPath({ id: id }))
  }

  const source = useTrainingsDataTable(
    [
      {
        identifier: 'name',
        kind: ProjectionKind.Path,
        title: i18n.t('trainings.trainings_table.name'),
        path: 'name',
      },
      {
        identifier: 'competencies',
        kind: ProjectionKind.Custom,
        title: i18n.t('trainings.trainings_table.competencies'),
        template: '{{ competencyIds }}',
        render: ({ competencies }) => {
          return <ItemsTags itemsName={competencies.map((competency) => competency.name)} />
        },
      },
      {
        identifier: 'categories',
        kind: ProjectionKind.Custom,
        title: i18n.t('trainings.trainings_table.tags'),
        template: '{{ categories }}',
        hidden: true,
        render: ({ categories }) => {
          return <ItemsTags itemsName={categories.map((category) => category.name)} />
        },
      },
      {
        identifier: 'modality',
        kind: ProjectionKind.Custom,
        title: i18n.t('trainings.training_catalog.datatable.type'),
        template: '{{ startDate }}',
        render: ({ sessions }) => {
          return <Modality sessions={sessions} />
        },
      },
      {
        identifier: 'startDate',
        kind: ProjectionKind.Custom,
        title: i18n.t('trainings.training_catalog.datatable.start_date'),
        template: '{{ startDate }}',
        render: ({ sessions }) => {
          return <StartDate sessions={sessions} />
        },
      },
      {
        identifier: 'endDate',
        kind: ProjectionKind.Custom,
        title: i18n.t('trainings.training_catalog.datatable.end_date'),
        template: '{{ endDate }}',
        render: ({ sessions }) => {
          return <EndDate sessions={sessions} />
        },
      },
      {
        identifier: 'actions',
        kind: ProjectionKind.Decorative,
        render: ({ id }) => {
          return (
            <Box flexDirection="row" gap="s8" alignItems="center" justifyContent="flexEnd">
              <Button type="secondary" onClick={() => onRequestTraining(id)}>
                {i18n.t('trainings.training_catalog.datatable.request')}
              </Button>
              <Button.Router
                type="secondary"
                to={navigation.trainings.catalog.detail.overview.fullPath({ trainingId: id })}
                iconName="arrow-right"
              />
            </Box>
          )
        },
      },
    ],
    { variables: { catalog: true, search: debouncedSearch } }
  )

  return (
    <DataTable
      filters={[
        competenciesFilter(source, 'catalogCompetenciesIdentifier', competencies),
        modalityFilter(source),
        dateFilter(source),
        categoriesFilter(source, categories),
      ]}
      title={i18n.t('trainings.my_trainings_revamp.overview.courses_datatable.title')}
      source={source}
      disableDefaultActions
      actions={
        <Button.Router to={navigation.trainings.catalog.list.new.fullPath()}>
          {i18n.t('trainings.training_catalog.request_box.title')}
        </Button.Router>
      }
      search={{
        placeholder: i18n.t('common.search'),
        value: search,
        onChange: onSearch,
      }}
    />
  )
}
